import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useI18n } from '../../i18n';

interface EnergyByMonth {
  month: string;
  year: number;
  totalEnergy: number;
  transactionCount?: number;
}

interface EnergyByMonthChartProps {
  data: EnergyByMonth[];
  loading?: boolean;
}

function EnergyByMonthChart({ data, loading }: EnergyByMonthChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t, locale } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Check if we have energy data or only transaction counts
  const hasEnergyData = data.some(d => d.totalEnergy > 0);

  const categories = data.map(d => {
    const [year, month] = d.month.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
  });

  // Use transaction count if no energy data, otherwise use energy
  const chartData = hasEnergyData 
    ? data.map(d => d.totalEnergy)
    : data.map(d => d.transactionCount || 0);

  const chartTitle = hasEnergyData ? t('leg2_monthly_consumption_kwh') : t('leg2_monthly_transactions');
  const chartSubtitle = hasEnergyData 
    ? t('leg2_monthly_consumption_sub')
    : t('leg2_monthly_sessions_sub');
  const yAxisTitle = hasEnergyData ? t('leg2_energy_kwh') : t('leg2_transactions');
  const tooltipFormat = hasEnergyData 
    ? `${t('energy')}: <b>{point.y:.2f}</b> kWh`
    : `${t('leg2_transactions')}: <b>{point.y}</b>`;
  const yAxisFormat = hasEnergyData ? '{value} kWh' : '{value}';

  const options: Highcharts.Options = {
    chart: {
      type: 'areaspline',
      height: 300,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: chartTitle,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: chartSubtitle,
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        rotation: -45,
      },
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb',
    },
    yAxis: {
      title: {
        text: yAxisTitle,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: yAxisFormat,
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      headerFormat: '<b>{point.key}</b><br/>',
      pointFormat: tooltipFormat,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#10b981',
      borderRadius: 8,
      shadow: true,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: '#10b981',
        },
        lineWidth: 3,
        color: '#10b981',
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(16, 185, 129, 0.4)'],
            [1, 'rgba(16, 185, 129, 0.05)'],
          ],
        },
      },
    },
    series: [{
      type: 'areaspline',
      name: hasEnergyData ? t('energy') : t('leg2_transactions'),
      data: chartData,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
      {data.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-4">
          {t('leg2_no_data_available')}
        </div>
      )}
    </div>
  );
}

export default EnergyByMonthChart;
